import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { z } from "zod";
import {
  ArrowRight,
  CheckCircle2,
  Inbox,
  Loader2,
  MailCheck,
  RefreshCw,
  ShieldAlert,
} from "lucide-react";

import { Header } from "@/components/Header";
import logo from "@/assets/logo.svg";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import {
  navigateAfterAuth,
  parsePostAuthRedirect,
} from "@/lib/post-auth-redirect";
import { toastError } from "@/lib/toast";

const searchSchema = z.object({
  email: z.string().optional(),
  redirect: z.string().optional(),
});

export const Route = createFileRoute("/verify-email")({
  validateSearch: searchSchema,
  head: () => ({
    meta: [
      { title: "Verify your email — Real Pranks" },
      {
        name: "description",
        content: "Click the verification link we sent to your email.",
      },
    ],
  }),
  component: VerifyEmailPage,
});

const RESEND_COOLDOWN_SECONDS = 30;
const POLL_INTERVAL_MS = 3000;

function VerifyEmailPage() {
  const search = Route.useSearch();
  const redirect = parsePostAuthRedirect(search.redirect);
  const navigate = useNavigate();
  const { user, loading, signedIn, sendCurrentUserEmailVerification } =
    useAuth();

  const [verified, setVerified] = useState(false);
  const [checking, setChecking] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const email = search.email ?? user?.email ?? null;

  // Watch for the user clicking the link in their email (possibly in another
  // tab). `reload()` refreshes the token claims on the same User instance.
  useEffect(() => {
    if (!user || verified) return;
    if (user.emailVerified) {
      setVerified(true);
      return;
    }
    const interval = setInterval(() => {
      user
        .reload()
        .then(() => {
          if (user.emailVerified) setVerified(true);
        })
        .catch(() => {
          /* transient network errors are fine; we poll again */
        });
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [user, verified]);

  // Resend cooldown ticker.
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleManualCheck = useCallback(async () => {
    if (!user) return;
    setChecking(true);
    try {
      await user.reload();
      if (user.emailVerified) {
        setVerified(true);
      } else {
        toast.info(
          "Not verified yet. Click the link in your email, then try again.",
        );
      }
    } catch {
      toastError("Could not check verification status. Try again in a moment.", {
        flow: "verify-email-manual-check",
      });
    } finally {
      setChecking(false);
    }
  }, [user]);

  const handleResend = useCallback(async () => {
    setResending(true);
    const result = await sendCurrentUserEmailVerification();
    setResending(false);
    if ("error" in result) {
      toastError(result.error, { flow: "verify-email-resend" });
      return;
    }
    setCooldown(RESEND_COOLDOWN_SECONDS);
    toast.success(result.message ?? "Verification email sent.");
  }, [sendCurrentUserEmailVerification]);

  const handleContinue = useCallback(() => {
    navigateAfterAuth(navigate, redirect);
  }, [navigate, redirect]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="mx-auto flex w-full max-w-[92%] flex-col gap-6 px-4 py-8 sm:max-w-md">
        <img src={logo} alt="" className="mx-auto h-16 w-auto pt-4" />

        {verified ? (
          <div className="flex flex-col items-center gap-4 rounded-xl border border-border bg-card p-8 text-center text-card-foreground">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-green-500/10">
              <CheckCircle2 className="h-7 w-7 text-green-500" />
            </div>
            <div>
              <h1 className="text-xl font-bold leading-tight">
                Email verified!
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {email ? (
                  <>
                    <span className="font-medium text-foreground">{email}</span>{" "}
                    is confirmed.{" "}
                  </>
                ) : null}
                Your account is all set.
              </p>
            </div>
            <Button onClick={handleContinue} className="h-11 w-full gap-2">
              Continue
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <>
            <div className="text-center">
              <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-secondary text-secondary-foreground">
                <MailCheck className="h-6 w-6" />
              </div>
              <h1 className="text-[1.4rem] font-bold leading-tight">
                Check your inbox
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                We sent a verification link
                {email ? (
                  <>
                    {" "}
                    to{" "}
                    <span className="font-medium text-foreground">{email}</span>
                  </>
                ) : null}
                . Click it to finish creating your account — this page updates
                automatically once you do.
              </p>
            </div>

            <div className="flex flex-col gap-5 rounded-xl border border-border bg-card p-5 text-card-foreground">
              <div className="flex items-center justify-center gap-2 rounded-lg bg-secondary/50 px-3 py-2.5 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Waiting for you to click the link…
              </div>

              <Button
                type="button"
                onClick={handleManualCheck}
                disabled={checking || !signedIn}
                className="h-11 gap-2"
              >
                {checking ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                I clicked the link — check now
              </Button>

              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resending || cooldown > 0 || !signedIn}
                  className="font-medium text-foreground underline-offset-4 hover:underline disabled:opacity-60"
                >
                  {resending
                    ? "Sending…"
                    : cooldown > 0
                      ? `Resend in ${cooldown}s`
                      : "Resend email"}
                </button>
                <Link
                  to="/signup"
                  className="font-medium text-foreground underline-offset-4 hover:underline"
                >
                  Use a different email
                </Link>
              </div>

              <div className="flex items-start gap-2.5 rounded-lg border border-border bg-background/50 px-3 py-2.5">
                <Inbox className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <p className="text-xs leading-relaxed text-muted-foreground">
                  Can't find it? Check your spam or promotions folder. The email
                  comes from Real Pranks and the link opens right back here.
                </p>
              </div>

              {!loading && !signedIn && (
                <div className="flex items-start gap-2.5 rounded-lg border border-amber-300/50 bg-amber-50 px-3 py-2.5 dark:border-amber-500/30 dark:bg-amber-500/10">
                  <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                  <p className="text-xs leading-relaxed text-amber-900 dark:text-amber-200">
                    Your session ended, so we can't resend from here.{" "}
                    <Link
                      to="/login"
                      className="font-medium underline underline-offset-4"
                    >
                      Sign in
                    </Link>{" "}
                    to resend — or just click the link in the email you already
                    have; it works either way.
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
